import {
  Asset,
  Button,
  Checkbox,
  InfoIcon,
  Loading,
  Modal,
  ModalLoading,
} from '@clearcover/atlas';
import { useAuth0 } from 'Auth';
import { ConfirmationModal, FormInput } from 'Shared/components';
import { convertDateIgnoringLocalTimeZone, formatMoney } from 'Shared/utils';
import { Formik } from 'formik';
import { DateTime } from 'luxon';
import React, { FC, useContext, useState } from 'react';
import { useParams } from 'react-router-dom';
import * as Yup from 'yup';

import { useLazyApi } from 'Shared/services/Api';
import { useNewApiV2 } from 'Shared/services/Api/makeApiHttp';

import { EffectivePeriodType } from '@clearcover/core-app-types';
import { PolicyContext } from 'Policies/utils';
import { DatePickerModal } from 'Shared/components/DatePickerModal';
import doorStyles from '../styles.module.css';
import { getLatestDate, useCancellation } from './hooks';
import styles from './styles.module.css';

const CustomerCancellationFormSchema = Yup.object().shape({
  cancellationDateType: Yup.string().required('Required'),
  cancellationDate: Yup.string(),
  reason: Yup.string().required('Please select a reason.'),
});

const ClearcoverCancellationFormSchema = Yup.object().shape({
  reason: Yup.string().required('Please select a reason.'),
  additionalNote: Yup.string(),
});

type PolicyCancellationFormProps = {
  closeForm: (cancelDraft?: boolean) => Promise<void>;
  initiationType: 'customer_requested' | 'clearcover_initiated';
  effectivePeriod: EffectivePeriodType;
  setCancelPolicyDraftId: (draftId: string) => void;
  renewalDraft?: { id: string };
  isFutureEffective: boolean;
};

export const PolicyCancellationForm: FC<PolicyCancellationFormProps> = ({
  closeForm,
  initiationType,
  effectivePeriod,
  setCancelPolicyDraftId,
  renewalDraft,
  isFutureEffective,
}) => {
  const {
    refetchHandler,
    details: { filingIdentifier },
  } = useContext(PolicyContext);
  const [{ data, loading }] = useNewApiV2<{
    reasons: Record<
      'customer_requested' | 'clearcover_initiated',
      Record<string, string>
    >;
  }>({
    url: `/cancellation/options?productFilingIdentifier=${filingIdentifier}`,
  });

  if (loading) return <ModalLoading />;
  if (initiationType === 'customer_requested') {
    return (
      <CustomerRequestedCancellationForm
        closeForm={closeForm}
        refetchHandler={refetchHandler}
        reasons={data.reasons[initiationType]}
        setCancelPolicyDraftId={setCancelPolicyDraftId}
        endDate={effectivePeriod.endsAt}
        renewalDraft={renewalDraft}
        isFutureEffective={isFutureEffective}
      />
    );
  }
  if (initiationType === 'clearcover_initiated') {
    return (
      <ClearcoverRequestedCancellationForm
        closeForm={closeForm}
        refetchHandler={refetchHandler}
        reasons={data.reasons[initiationType]}
      />
    );
  }
  return <></>;
};

const CustomerRequestedCancellationForm: FC<{
  reasons: Record<string, string>;
  closeForm: (cancelDraft?: boolean) => Promise<void>;
  refetchHandler: () => void;
  setCancelPolicyDraftId: (draftId: string) => void;
  endDate: string;
  renewalDraft?: { id: string };
  isFutureEffective: boolean;
}> = ({
  reasons,
  closeForm,
  refetchHandler,
  setCancelPolicyDraftId,
  endDate,
  renewalDraft,
  isFutureEffective,
}) => {
  const { user: currentUser } = useAuth0();
  const {
    details: { effectiveDate, timeZone },
  } = useContext(PolicyContext);

  const [externalUserConfirmation, setExternalUserConfirmation] = useState(
    false
  );

  const {
    data: { draftId, totalAmountDue, summary, cancellationNotice },
    applyCancelPolicy,
    updateCancellationDraft,
    terminatePolicyRenewal,
    loading,
    errorMessage,
  } = useCancellation(setCancelPolicyDraftId);

  const currentDate = new Date();
  currentDate.setHours(0, 0, 0, 0);

  return (
    <Formik
      initialValues={{
        reason: '',
        cancellationDateType: '',
        cancellationDate: '',
      }}
      validationSchema={CustomerCancellationFormSchema}
      onSubmit={values => {
        if (values.cancellationDateType === 'nonrenewal') {
          terminatePolicyRenewal().then(() => {
            closeForm().then(refetchHandler);
          });
        } else {
          applyCancelPolicy(values.reason, values.cancellationDate).then(() => {
            closeForm(false).then(() => {
              refetchHandler();
            });
          });
        }
      }}
      validateOnChange={true}
    >
      {({
        values,
        errors,
        isValid,
        setFieldValue,
        submitForm,
        isSubmitting,
        validateForm,
      }) => (
        <div data-testid="policy-cancellation-form" className={doorStyles.door}>
          <div className={doorStyles.header}>
            <h2>Cancel Policy</h2>
          </div>
          <div className={doorStyles.body}>
            {reasons && (
              <FormInput
                data-testid="reasons-select"
                variant="selectAutocomplete"
                name="reason"
                onChange={value => {
                  setFieldValue('reason', value);
                  if (values.cancellationDate !== '') {
                    updateCancellationDraft(
                      value,
                      values.cancellationDate
                    ).then(() => {
                      validateForm();
                    });
                  }
                }}
                value={values.reason}
                label="Reason for Cancellation"
                options={reasons}
                errorMessage={errors.reason}
              />
            )}

            <div className={styles.radioButtonContainer}>
              <FormInput
                variant="radio"
                data-testid="immediate-cancellation"
                disabled={loading || isFutureEffective}
                name="cancellation-date"
                className={styles.paymentRadioGroup}
                value={'immediate'}
                label="Effective Immediately"
                checked={values.cancellationDateType === 'immediate'}
                onChecked={() => {
                  setFieldValue('cancellationDateType', 'immediate');
                  setFieldValue('cancellationDate', new Date().toISOString());
                  updateCancellationDraft(values.reason).then(() => {
                    validateForm();
                  });
                }}
              />
            </div>

            <div className={styles.radioButtonContainer}>
              <FormInput
                data-testid="scheduled-date"
                disabled={loading}
                variant="radio"
                name="cancellation-date"
                className={styles.paymentRadioGroup}
                value={'scheduled'}
                label="Scheduled"
                checked={values.cancellationDateType === 'selected-date'}
                onChecked={() => {
                  const currentDateOrStartOfPolicy = getLatestDate(
                    DateTime.fromJSDate(new Date(), {
                      zone: timeZone,
                    }).startOf('day'),
                    DateTime.fromISO(effectiveDate, {
                      zone: timeZone,
                      setZone: true,
                    })
                  );

                  setFieldValue('cancellationDateType', 'selected-date');
                  setFieldValue(
                    'cancellationDate',
                    currentDateOrStartOfPolicy.toLocal()
                  );
                  updateCancellationDraft(
                    values.reason,
                    currentDateOrStartOfPolicy.toISO()
                  ).then(() => {
                    validateForm();
                  });
                }}
              />
              {values.cancellationDateType === 'selected-date' && (
                <DatePickerModal
                  startDate={new Date(values.cancellationDate)}
                  disabled={loading}
                  classNames={styles.datePicker}
                  label={'Termination Date'}
                  errorMessage={errors.cancellationDate}
                  minDateAllowed={
                    currentUser?.isInternalUser
                      ? convertDateIgnoringLocalTimeZone(
                          effectiveDate,
                          timeZone
                        )
                      : currentDate
                  }
                  maxDateAllowed={convertDateIgnoringLocalTimeZone(
                    endDate,
                    timeZone
                  )}
                  onDateChange={date => {
                    const selectedDateOrStartOfPolicy = DateTime.fromJSDate(
                      new Date(date),
                      {
                        zone: timeZone,
                      }
                    ).startOf('day');
                    setFieldValue(
                      'cancellationDate',
                      selectedDateOrStartOfPolicy.toISO()
                    );
                    updateCancellationDraft(
                      values.reason,
                      selectedDateOrStartOfPolicy.toISO()
                    );
                  }}
                />
              )}
            </div>

            {renewalDraft && (
              <div className={styles.radioButtonContainer}>
                <FormInput
                  data-testid="last-day-of-policy"
                  disabled={loading}
                  variant="radio"
                  name="cancellation-date"
                  className={styles.paymentRadioGroup}
                  value={'last-day-of-policy'}
                  label="Last Day of Policy"
                  checked={values.cancellationDateType === 'nonrenewal'}
                  onChecked={() => {
                    setFieldValue('cancellationDateType', 'nonrenewal');
                  }}
                />
              </div>
            )}
            {loading ? (
              <Loading />
            ) : (
              draftId !== '' &&
              values.cancellationDateType !== 'nonrenewal' && (
                <>
                  {summary.length > 0 && (
                    <div className={styles.summary}>
                      <h3>Summary</h3>
                      {summary.map(({ label, amount }, i) => (
                        <div
                          key={label}
                          data-testid={`summary-pair-${i}`}
                          className={styles.summaryPair}
                        >
                          <div
                            className={styles.summaryLabel}
                            data-testid={`summary-pair-${i}-label`}
                          >
                            {label}
                          </div>
                          <div
                            data-testid={`summary-pair-${i}-amount`}
                            className={styles.summaryAmount}
                          >
                            {amount}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  <div className={styles.refundContainer}>
                    <div className={styles.refundLabel}>
                      {totalAmountDue <= 0 ? 'Refund Amount' : 'Amount Owed'}
                    </div>
                    <div
                      data-testid={'refund-amount'}
                      className={styles.refundAmount}
                    >
                      {formatMoney(Math.abs(totalAmountDue))}
                    </div>
                  </div>
                </>
              )
            )}

            {cancellationNotice && (
              <div
                className={styles.minimumPremiumNotice}
                data-testid="cancellation-notice"
              >
                {cancellationNotice}
              </div>
            )}
            {totalAmountDue <= 0 && (
              <div className={styles.paymentNotice}>
                <Asset
                  icon={InfoIcon}
                  description={'Payment Notice'}
                  height={16}
                  width={16}
                  className={styles.infoIcon}
                />
                <p>
                  Payment will be automatically refunded to the default payment
                  method on file for the customer. If the refund fails to post
                  to that account, customer will receive a check in the mail in
                  7-14 days.
                </p>
              </div>
            )}
            {!currentUser?.isInternalUser && (
              <div>
                <Checkbox
                  name="externalUserConfirmation"
                  label="* I am authorized by the customer to cancel this policy on their behalf"
                  value={externalUserConfirmation}
                  onChange={value => setExternalUserConfirmation(value)}
                />
              </div>
            )}
            <p className={styles.warning}>
              Tapping below will remove all coverage as of the effective date
              and time listed above.
            </p>
            <p className={styles.error}>{errorMessage}</p>
            <Modal
              toggle={openModal => (
                <Button
                  block
                  data-testid="open-cancel-policy-modal"
                  variant="danger"
                  isSubmitting={isSubmitting}
                  disabled={
                    !isValid ||
                    values.reason === '' ||
                    loading ||
                    (!!currentUser &&
                      !currentUser.isInternalUser &&
                      !externalUserConfirmation)
                  }
                  onClick={openModal}
                >
                  {values.cancellationDateType === 'nonrenewal'
                    ? 'Cancel Renewal Draft'
                    : 'Cancel Policy'}
                </Button>
              )}
            >
              {close => (
                <ConfirmationModal
                  onSubmit={submitForm}
                  error={errorMessage}
                  close={close}
                  isSubmitting={isSubmitting}
                  headerText="Please confirm cancellation"
                  bodyText="You are about to cancel this policy. Please confirm that you would like to cancel."
                />
              )}
            </Modal>
            <Button
              block
              link
              onClick={() => closeForm()}
              data-testid="do-not-cancel-policy-button"
              className={styles.doNotCancelButton}
            >
              Don't Cancel Policy
            </Button>
          </div>
        </div>
      )}
    </Formik>
  );
};

const ClearcoverRequestedCancellationForm: FC<{
  reasons: Record<string, string>;
  closeForm: () => Promise<void>;
  refetchHandler: () => void;
}> = ({ reasons, closeForm, refetchHandler }) => {
  const { id: policyId } = useParams<{ id: string }>();
  const [, cancelPolicy] = useLazyApi<{}>({
    method: 'POST',
    url: `/policies/cancellation/clearcoverInitiated`,
  });
  return (
    <Formik
      initialValues={{
        reason: '',
        additionalNote: '',
      }}
      validationSchema={ClearcoverCancellationFormSchema}
      onSubmit={({ reason, additionalNote }, { setStatus, setSubmitting }) => {
        cancelPolicy({
          data: {
            policyId,
            cancellationReason: {
              code: reason,
              description: reasons[reason],
            },
            additionalNote,
          },
        })
          .then(() => {
            closeForm().then(refetchHandler);
          })
          .catch(({ message }) => {
            setStatus(message);
            setSubmitting(false);
          });
      }}
      validateOnChange={true}
    >
      {({
        values,
        errors,
        status,
        isValid,
        setFieldValue,
        submitForm,
        isSubmitting,
        handleChange,
      }) => (
        <div className={doorStyles.door}>
          <div className={doorStyles.header}>
            <h2>Clearcover Initiated Cancellation</h2>
          </div>

          <div className={doorStyles.body}>
            {reasons && (
              <FormInput
                variant="selectAutocomplete"
                name="reason"
                onChange={value => {
                  setFieldValue('reason', value);
                }}
                value={values.reason}
                label="Reason for Cancellation"
                options={reasons}
                errorMessage={errors.reason}
              />
            )}

            <FormInput
              variant="textarea"
              name="additionalNote"
              onChange={handleChange}
              value={values.additionalNote}
              label="Additional Notes"
              maxLength={500}
              errorMessage={errors.additionalNote}
            />
            <p className={styles.error}>
              Warning: Notes are shared with customer
            </p>

            <p className={styles.error}>{status}</p>
            <Modal
              toggle={openModal => (
                <Button
                  block
                  variant="danger"
                  isSubmitting={isSubmitting}
                  data-testid="open-cancel-policy-modal"
                  disabled={!isValid || values.reason === ''}
                  onClick={openModal}
                >
                  Cancel Policy
                </Button>
              )}
            >
              {close => (
                <ConfirmationModal
                  onSubmit={submitForm}
                  error={status}
                  close={close}
                  isSubmitting={isSubmitting}
                  headerText="Please confirm cancellation"
                  bodyText="You are about to cancel this policy. Please confirm that you would like to cancel."
                />
              )}
            </Modal>
            <Button
              block
              link
              onClick={closeForm}
              data-testid="do-not-cancel-policy-button"
              className={styles.doNotCancelButton}
            >
              Don't Cancel Policy
            </Button>
          </div>
        </div>
      )}
    </Formik>
  );
};
